import { Button, IconButton, TextField } from '@zep-us/zus';
import { ArrowsUpdownIcon, FilterIcon, MagnifingglassIcon, XIcon } from '@zep-us/zus-icons';
import { useTranslation } from 'next-i18next';
import { QUIZ_BANK_SORT_KEYS } from '../constants';
import { type QuizBankSortKey } from '../types';
import { QuizBankDivider } from './QuizBankDivider';
interface QuizBankResultToolbarProps {
  /** 입력 중인 검색어 (제출 전) */
  searchDraft: string;
  onSearchDraftChange: (value: string) => void;
  onSearchSubmit: () => void;
  onSearchClear: () => void;
  resultCount: number;
  sort: QuizBankSortKey;
  onSortChange: (sort: QuizBankSortKey) => void;
  /** 모바일에서 필터 화면을 연다 */
  onOpenFilter?: () => void;
}

/**
 * 목록 위 컨트롤 줄 — 검색 · 문제 수 · 정렬 (Figma).
 * 정렬은 서버가 정답률 두 방향만 지원해 드롭다운 대신 토글 버튼이다.
 */
export const QuizBankResultToolbar = ({
  searchDraft,
  onSearchDraftChange,
  onSearchSubmit,
  onSearchClear,
  resultCount,
  sort,
  onSortChange,
  onOpenFilter
}: QuizBankResultToolbarProps) => {
  const {
    t
  } = useTranslation(['build']);
  return (
    // 모바일에서는 검색 입력만으로 폭을 다 써서 두 줄로 접는다.
    <div className="flex flex-col gap-xs pb-3xs pt-3xs tablet-sm:flex-row tablet-sm:items-center tablet-sm:justify-between tablet-sm:gap-sm" data-sentry-component="QuizBankResultToolbar" data-sentry-source-file="QuizBankResultToolbar.tsx">
      <div className="w-full min-w-px tablet-sm:max-w-[500px]">
        <TextField.Root data-sentry-element="unknown" data-sentry-source-file="QuizBankResultToolbar.tsx">
          <TextField.Input value={searchDraft} placeholder={t('build:quizBank.toolbar.searchPlaceholder')} rightSlot={searchDraft ? <IconButton variant="transparent" intent="neutral" size="xs" aria-label={t('build:quizBank.toolbar.searchClear')} onClick={onSearchClear}>
                  <XIcon size="sm" />
                </IconButton> : <MagnifingglassIcon size="md" className="fill-assistive" />} onChange={event => onSearchDraftChange(event.target.value)} onKeyDown={event => {
            if (event.key !== 'Enter') return;
            // 한글 IME 는 조합 확정에도 Enter 를 쓴다 — 미완성 검색어로 요청이 나가지 않게 막는다.
            // (keyCode 229 는 isComposing 이 없는 구형 폴백)
            if (event.nativeEvent.isComposing || event.keyCode === 229) return;
            onSearchSubmit();
          }} data-sentry-element="unknown" data-sentry-source-file="QuizBankResultToolbar.tsx" />
        </TextField.Root>
      </div>

      <div className="flex shrink-0 items-center gap-2xs">
        {onOpenFilter && <>
            <Button variant="outlined" intent="neutral" size="md" className="shrink-0 tablet-sm:hidden" leftSlot={<FilterIcon size="md" />} onClick={onOpenFilter}>
              {t('build:quizBank.filter.open')}
            </Button>
            <QuizBankDivider className="h-[16px] tablet-sm:hidden" />
          </>}

        <span className="shrink-0 whitespace-nowrap px-sm text-body-md-semibold tabular-nums text-neutral">
          {t('build:quizBank.toolbar.resultCount', {
            n: resultCount.toLocaleString()
          })}
        </span>

        <QuizBankDivider className="h-[16px]" data-sentry-element="QuizBankDivider" data-sentry-source-file="QuizBankResultToolbar.tsx" />

        <Button variant="transparent" intent="neutral" size="md" className="shrink-0" leftSlot={<ArrowsUpdownIcon size="md" />} onClick={() => onSortChange(sort === 'answerRateDesc' ? 'answerRateAsc' : 'answerRateDesc')} data-sentry-element="Button" data-sentry-source-file="QuizBankResultToolbar.tsx">
          {t(QUIZ_BANK_SORT_KEYS[sort])}
        </Button>
      </div>
    </div>
  );
};